import React from 'react';
import { GlobalRuleType } from 'Common/enums';
import { Row } from 'react-table';
import _ from 'lodash';
import { Button, Loading, Paper } from '@brighterion/br-ui';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { t } from 'i18next';
import SplitViewHeader from 'Components/Table/SplitView/Header';
import SplitView from 'Components/Table/SplitView';
import GlobalRuleDetails from 'Components/GlobalRuleDetails';
import EditRuleButton from 'Components/EditRuleButton';
import { UseSplitViewForm } from 'Components/Table/hooks/useSplitViewForm';
import SplitRevisionHistory from 'Components/SplitRevisionHistory';
import SplitRuleStatement from 'Components/SplitRuleStatement';
import useGetList from 'Hooks/query-hooks/list/useGetList';
import { shouldShowCreateRule } from 'Selectors';
import { RoleService } from 'Services';
import TenantOverrideButton from 'Components/TenantOverrideButton';
import { useHistory, useLocation } from 'react-router-dom';

import styles from './SplitViewContent.css';

type UseSplitViewFormOptions = {
    readOnly: boolean;
    allowOverride: boolean;
    isLandlordDeployment: boolean;
    isRevisionHistoryVisible: boolean;
};

interface Props extends UseSplitViewForm<GlobalRuleSplitViewFormState> {
    dataTestId: string;
    rows: Row<Rule>[];
    selectedRow: Row<Rule>;
    toggleRowSelected(id: string, toggle: boolean): void;
    deploymentVersion?: number;
    mode: string;
    selectViewOption?: string;
    options?: Partial<UseSplitViewFormOptions>;
}

const SplitViewContent = ({
    dataTestId,
    selectedRow,
    rows,
    toggleRowSelected,
    splitViewFormState,
    setRowId,
    deploymentVersion,
    mode,
    selectViewOption,
    options
}: Props) => {
    const { isOpen } = splitViewFormState;
    const closeSplitView = () => {
        setRowId(null);
    };
    const history = useHistory();
    const location = useLocation();
    const { original } = selectedRow;
    const globalRuleIsActive = _.get(original, 'parameters.active');
    const ruleIsActive = isOpen || globalRuleIsActive;

    const handleViewRuleTest = () => {
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Global Rule Details - View Rule Test',
                text: 'View Global Rule Test'
            }
        });
        history.push(
            // eslint-disable-next-line max-len
            `${location.pathname}/rule-tester/view/${selectedRow.original.id}?selectedRowId=${selectedRow.id}`
        );
    };

    // RevisionHistory and current rule may have list changes or list rules
    // So we have to retrieve lists to find corresponding list names
    const listAPI = useGetList();
    if (listAPI.isError) return <>{t('Error Fetching', { fieldName: 'Lists' })}</>;
    if (listAPI.isLoading || listAPI.isFetching)
        return (
            <div className={styles.spinner}>
                <Loading />
            </div>
        );

    // The state of "createRule" feature flag will control user's edit access
    // forbid adhoc edit when the current rule is adhoc and createRule feature flag is off
    const forbidAdHocEdit = !!selectedRow.original?.nonTemplated && !shouldShowCreateRule();
    const getSplitViewLeftElement = () => {
        if (mode === 'deployed') {
            return null;
        }
        if (
            (options?.isLandlordDeployment && options?.allowOverride && !options?.readOnly) ||
            (selectedRow.original.isTenantOverride && !options?.readOnly)
        ) {
            return (
                <TenantOverrideButton
                    id={selectedRow.original.id}
                    selectedRowId={selectedRow.id}
                    hasOverride={selectedRow.original.isTenantOverride}
                />
            );
        }
        if (!options?.readOnly && !forbidAdHocEdit && !selectedRow.original?.status) {
            return (
                <EditRuleButton
                    id={selectedRow.original.id}
                    selectedRowId={selectedRow.id}
                    selectViewOption={selectViewOption}
                    ruleType={
                        selectedRow.original.expression ? GlobalRuleType.ADVANCED_RULE : GlobalRuleType.SIMPLE_RULE
                    }
                    nonTemplated={selectedRow.original?.nonTemplated}
                />
            );
        }

        if (!RoleService.hasEditRuleOrBulkUploadAccess && RoleService.hasViewRuleTestRole && !deploymentVersion) {
            // View rule test should only be avaible on non deployed versions
            return (
                <Button
                    dataTestId='edit-rule-button'
                    variant='tertiary'
                    onClick={handleViewRuleTest}
                    onKeyDown={handleViewRuleTest}
                >
                    {t('View Rule Test')}
                </Button>
            );
        }
        return null;
    };

    return (
        <>
            <SplitViewHeader dataTestId={dataTestId}>
                <SplitViewHeader.Title dataTestId={`${dataTestId}-title`} title='Global Rule Details' />
                <SplitViewHeader.Close dataTestId={`${dataTestId}-close`} closeSplitView={closeSplitView} />
            </SplitViewHeader>
            <SplitView.Navigation
                dataTestId='splitview-nav'
                selectedRow={selectedRow}
                rows={rows}
                toggleRowSelected={toggleRowSelected}
                leftElement={getSplitViewLeftElement()}
            />
            <>
                <Paper className={styles.contentPanel}>
                    <GlobalRuleDetails ruleIsActive={ruleIsActive} dataTestId={dataTestId} globalRule={original} />
                </Paper>
                <SplitRuleStatement
                    outputDescription={original?.outputDescription}
                    ruleInputs={original?.inputs}
                    parameters={original?.parameters}
                    lists={original?.lists}
                    listData={listAPI.data?.result}
                    conclusionValues={original._formatted?.conclusionValues}
                    expression={original?.expression}
                />
                {options?.isRevisionHistoryVisible && (
                    <SplitRevisionHistory
                        level='ACQUIRER'
                        deploymentVersion={deploymentVersion}
                        key={original?.id}
                        ruleId={original?.id}
                        listData={listAPI.data?.result}
                        isLandlordDeployment={options?.isLandlordDeployment}
                    />
                )}
            </>
        </>
    );
};

export default React.memo(SplitViewContent);
